import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
} from 'react';
import type { Document, SubmissionValidationResultsResponse } from '@/api/v2Client';
import {
  countsFrom,
  failingEntries,
  formatValidationCounts,
  hasIssues,
  type FailingEntry,
} from '@/lib/validationSummary';
import { RuleOriginTag } from './RuleOriginTag';

/**
 * Bottom-pinned validation WORKLIST for the Compiler workspace.
 *
 * phase-validation-surfaces (Sharon, 2026-07-23) — this used to be a thin
 * "notification bar" whose expanded view listed `documentsWithErrors`. Three
 * things were wrong, reconciled together here:
 *
 *  - **It dropped warnings.** The chip counted `errorCount` only and the drawer
 *    hid itself when `errorCount === 0` even with warnings outstanding. A
 *    warning a user never sees reaches the agency. The chip now reports
 *    `formatValidationCounts` (errors AND warnings) from one shared source.
 *
 *  - **Its detail opened empty.** The expanded panel mapped
 *    `documentsWithErrors`, which is empty whenever the current errors are
 *    SUBMISSION-scoped — as they are on Eupravia Seq 0001, where all four errors
 *    are manifest/filename checks with `targetType='Submission'` and no document
 *    id. So the panel rendered zero rows regardless of height. It now lists the
 *    actual failing ENTRIES (errors + warnings) with their messages, so there is
 *    always something to read.
 *
 *  - **It had no height of its own.** Sitting below a `flex:1` pane grid, the
 *    expanded content was starved. The panel is now `flex-shrink:0` with a real,
 *    session-persisted height you can drag from its top edge — a work surface,
 *    not a toast.
 *
 * ## Actionability (Sharon's worklist requirement)
 *
 * A DOCUMENT-targeted entry is a link: clicking it selects the offending
 * document in the CTD tree (via `onNavigateToDocument`). A SUBMISSION-targeted
 * entry (a manifest/filename check) carries a `details` payload but NO document
 * id and no section code, so it cannot resolve to a tree row — it is shown with
 * its full message but is deliberately not a link, rather than faking a
 * destination. Making those rows document-actionable needs the validation RULES
 * to emit per-document results; that is a backend change, noted in the report.
 */

const DRAWER_HEIGHT_KEY = 'autoectd-compiler-validation-drawer-height';
const DEFAULT_PANEL_HEIGHT = 200;
const MIN_PANEL_HEIGHT = 96;
const MAX_PANEL_HEIGHT = 520;

export interface CompilerNotificationDrawerProps {
  readonly documents: readonly Document[];
  readonly validation: SubmissionValidationResultsResponse | null;
  /**
   * Selects the given document in the CTD tree (and opens the Inspector on it).
   * Called for the first unconfirmed doc from the unconfirmed chip, or for a
   * document-targeted validation entry the user clicks in the worklist.
   */
  readonly onNavigateToDocument: (documentId: string) => void;
  /** Opens the full ValidationReportModal (the "everything" view). */
  readonly onOpenValidationReport?: () => void;
  /**
   * phase-validation-surfaces item 1 — controlled open state so the toolbar
   * validation badge can open this worklist (its old target, the chrome
   * validation banner, is `display:none` when chrome is collapsed, which is why
   * the badge looked inert). Uncontrolled if omitted.
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /**
   * phase-compiler-six-tabs — render the worklist INLINE (no bottom-pinned bar,
   * no resize handle, always expanded) for the Validation tab's submission
   * scope. The persistent bottom drawer is retired by the six-tab workspace;
   * this is the same worklist content, embedded as the tab's body. Empty states
   * render honestly (no issues → "No open validation issues") instead of the
   * null the floating drawer returns, so the tab is never blank.
   */
  readonly embedded?: boolean;
}

const barStyle: CSSProperties = {
  borderTop: '1px solid var(--border)',
  background: 'var(--surface2)',
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 12,
  flexWrap: 'wrap',
};

const chipBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid',
};

function chipStyle(tone: 'warning' | 'error'): CSSProperties {
  return {
    ...chipBase,
    background: tone === 'error' ? '#fee2e2' : '#fef3c7',
    color: tone === 'error' ? '#991b1b' : '#92400e',
    borderColor: tone === 'error' ? '#fecaca' : '#fde68a',
  };
}

function readStoredHeight(): number {
  if (typeof window === 'undefined') return DEFAULT_PANEL_HEIGHT;
  try {
    const raw = window.sessionStorage.getItem(DRAWER_HEIGHT_KEY);
    if (!raw) return DEFAULT_PANEL_HEIGHT;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return DEFAULT_PANEL_HEIGHT;
    return Math.min(MAX_PANEL_HEIGHT, Math.max(MIN_PANEL_HEIGHT, n));
  } catch {
    return DEFAULT_PANEL_HEIGHT;
  }
}

export function CompilerNotificationDrawer(
  props: CompilerNotificationDrawerProps,
): ReactElement | null {
  // phase-compiler-six-tabs — embedded (Validation-tab) mode is a distinct,
  // self-contained surface: no floating bar, no resize, always expanded, honest
  // empty states. It owns NO hooks, so it is split from the floating drawer (a
  // hook after a prop-conditioned early return would break the rules of hooks).
  if (props.embedded) {
    const unconfirmed = props.documents.filter((d) => !d.ConfirmedAt);
    const counts = countsFrom(props.validation);
    const entries = failingEntries(props.validation);
    return (
      <div role="region" aria-label="Validation worklist" data-testid="validation-worklist-embedded">
        <WorklistContent
          unconfirmed={unconfirmed}
          entries={entries}
          summaryLabel={formatValidationCounts(counts)}
          documents={props.documents}
          onNavigateToDocument={props.onNavigateToDocument}
          {...(props.onOpenValidationReport ? { onOpenValidationReport: props.onOpenValidationReport } : {})}
        />
      </div>
    );
  }
  return <FloatingDrawer {...props} />;
}

/**
 * The bottom-pinned floating worklist (chips + drag-resizable expanded panel).
 * Holds every drawer hook, so it is a component of its own — the embedded mode
 * returns before this is ever mounted.
 */
function FloatingDrawer({
  documents,
  validation,
  onNavigateToDocument,
  onOpenValidationReport,
  open,
  onOpenChange,
}: CompilerNotificationDrawerProps): ReactElement | null {
  const unconfirmed = documents.filter((d) => !d.ConfirmedAt);
  const counts = countsFrom(validation);
  const entries = failingEntries(validation);

  // Controlled-or-uncontrolled open state.
  const [openInternal, setOpenInternal] = useState(false);
  const isOpen = open ?? openInternal;
  const setOpen = useCallback(
    (next: boolean) => {
      if (onOpenChange) onOpenChange(next);
      else setOpenInternal(next);
    },
    [onOpenChange],
  );

  // Session-persisted panel height (drag the top edge to resize).
  const [panelHeight, setPanelHeight] = useState<number>(readStoredHeight);
  const dragState = useRef<{ startY: number; startH: number } | null>(null);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(DRAWER_HEIGHT_KEY, String(panelHeight));
    } catch {
      // storage disabled / quota — the height still drives this session.
    }
  }, [panelHeight]);

  const onResizePointerMove = useCallback((e: globalThis.PointerEvent) => {
    const st = dragState.current;
    if (!st) return;
    // Dragging UP (smaller y) grows the panel.
    const delta = st.startY - e.clientY;
    const next = Math.min(MAX_PANEL_HEIGHT, Math.max(MIN_PANEL_HEIGHT, st.startH + delta));
    setPanelHeight(next);
  }, []);

  const onResizePointerUp = useCallback(() => {
    dragState.current = null;
    window.removeEventListener('pointermove', onResizePointerMove);
    window.removeEventListener('pointerup', onResizePointerUp);
  }, [onResizePointerMove]);

  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      dragState.current = { startY: e.clientY, startH: panelHeight };
      window.addEventListener('pointermove', onResizePointerMove);
      window.addEventListener('pointerup', onResizePointerUp);
    },
    [panelHeight, onResizePointerMove, onResizePointerUp],
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onResizePointerMove);
      window.removeEventListener('pointerup', onResizePointerUp);
    },
    [onResizePointerMove, onResizePointerUp],
  );

  // Nothing to surface → render nothing. NOTE: warnings count too now — the old
  // guard was `errorCount === 0`, which hid a submission with only warnings.
  if (unconfirmed.length === 0 && !hasIssues(counts)) {
    return null;
  }

  const summaryLabel = formatValidationCounts(counts);

  return (
    <div
      role="region"
      aria-label="Compiler validation worklist"
      data-testid="compiler-notification-drawer"
      // flex-shrink:0 so the pane grid above cannot starve this of height (the
      // regression fixed here). borderRadius kept; overflow visible so the
      // resize handle isn't clipped.
      style={{ borderRadius: '0 0 8px 8px', flexShrink: 0 }}
    >
      <div style={barStyle}>
        {unconfirmed.length > 0 && (
          <button
            type="button"
            data-testid="unconfirmed-chip"
            onClick={() => {
              const first = unconfirmed[0];
              if (first) onNavigateToDocument(first.Id);
            }}
            style={chipStyle('warning')}
            title="Jump to the first unconfirmed document"
          >
            <span aria-hidden="true">🔔</span>
            {unconfirmed.length} unconfirmed
          </button>
        )}
        {hasIssues(counts) && (
          <button
            type="button"
            data-testid="validation-errors-chip"
            onClick={() => setOpen(!isOpen)}
            style={chipStyle(counts.errorCount > 0 ? 'error' : 'warning')}
            aria-expanded={isOpen}
            title="Expand the validation worklist"
          >
            <span aria-hidden="true">⚠</span>
            {summaryLabel}
          </button>
        )}
        <button
          type="button"
          data-testid="drawer-toggle"
          onClick={() => setOpen(!isOpen)}
          aria-expanded={isOpen}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          {isOpen ? 'Hide ▾' : 'Show details ▸'}
        </button>
      </div>
      {isOpen && (
        <div
          data-testid="drawer-expanded"
          style={{
            background: 'var(--surface)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            height: panelHeight,
            minHeight: MIN_PANEL_HEIGHT,
          }}
        >
          {/* Drag the top edge to resize — the app's resize idiom, vertical. */}
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize validation worklist"
            data-testid="drawer-resize-handle"
            onPointerDown={onResizePointerDown}
            style={{
              height: 6,
              cursor: 'ns-resize',
              background: 'transparent',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}
          />
          <WorklistContent
            unconfirmed={unconfirmed}
            entries={entries}
            summaryLabel={summaryLabel}
            documents={documents}
            onNavigateToDocument={onNavigateToDocument}
            onOpenValidationReport={onOpenValidationReport}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The worklist body (unconfirmed list + failing entries) shared by the floating
 * drawer and the embedded Validation-tab mode. One source so the two surfaces
 * can never disagree about what counts as outstanding.
 */
function WorklistContent({
  unconfirmed,
  entries,
  summaryLabel,
  documents,
  onNavigateToDocument,
  onOpenValidationReport,
}: {
  unconfirmed: readonly Document[];
  entries: readonly FailingEntry[];
  summaryLabel: string;
  documents: readonly Document[];
  onNavigateToDocument: (documentId: string) => void;
  onOpenValidationReport?: () => void;
}): ReactElement {
  return (
    <div
      style={{
        padding: '8px 12px',
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
        fontSize: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {unconfirmed.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 6px', fontSize: 12 }}>Unconfirmed in this submission</h4>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {unconfirmed.map((d) => (
              <li key={d.Id} style={{ marginBottom: 2 }}>
                <button
                  type="button"
                  onClick={() => onNavigateToDocument(d.Id)}
                  data-testid={`drawer-unconfirmed-${d.Id}`}
                  style={linkButtonStyle}
                >
                  {d.FileName}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 6,
          }}
        >
          <h4 style={{ margin: 0, fontSize: 12 }}>
            Validation issues{entries.length > 0 ? ` — ${summaryLabel}` : ''}
          </h4>
          {onOpenValidationReport && (
            <button
              type="button"
              data-testid="drawer-open-report"
              onClick={onOpenValidationReport}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontSize: 12,
                padding: 0,
              }}
            >
              Full report ↗
            </button>
          )}
        </div>
        {entries.length === 0 ? (
          <p data-testid="drawer-no-issues" style={{ margin: 0, color: 'var(--text-muted)' }}>
            No open validation issues.
          </p>
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {entries.map((entry) => (
              <ValidationEntryRow
                key={entry.resultId}
                entry={entry}
                documents={documents}
                onNavigateToDocument={onNavigateToDocument}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const linkButtonStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  cursor: 'pointer',
  padding: 0,
  fontSize: 12,
  textAlign: 'left',
};

function ValidationEntryRow({
  entry,
  documents,
  onNavigateToDocument,
}: {
  entry: FailingEntry;
  documents: readonly Document[];
  onNavigateToDocument: (documentId: string) => void;
}): ReactElement {
  const isDocTarget = entry.targetType === 'Document';
  const doc = isDocTarget ? documents.find((d) => d.Id === entry.targetId) : undefined;
  const icon = entry.severity === 'error' ? '✗' : '⚠';
  const color = entry.severity === 'error' ? '#b91c1c' : '#92400e';
  const message = entry.message ?? entry.description ?? entry.ruleName ?? 'Validation issue';

  return (
    <li
      data-testid={`drawer-issue-${entry.resultId}`}
      data-severity={entry.severity}
      data-target-type={entry.targetType}
      style={{ display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.4 }}
    >
      <span aria-hidden="true" style={{ color, flexShrink: 0 }}>
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        {isDocTarget ? (
          // A document-targeted issue resolves to a tree row — make it a link.
          <button
            type="button"
            data-testid={`drawer-issue-nav-${entry.resultId}`}
            onClick={() => onNavigateToDocument(entry.targetId)}
            style={{ ...linkButtonStyle, fontWeight: 600 }}
            title="Select this document in the CTD tree"
          >
            {doc?.FileName ?? 'document'}
          </button>
        ) : (
          // A submission-targeted issue (manifest/filename check) has no single
          // document to jump to — show it plainly rather than fake a link.
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>Submission</span>
        )}
        <span style={{ color: 'var(--text-muted)' }}> — {message}</span>
        {/* phase-validation-rules-screen — an internal-check failure must not
            read as an agency rejection. */}
        <RuleOriginTag origin={entry.origin} />
      </span>
    </li>
  );
}
